/* eslint-disable no-cond-assign */
/* eslint-disable prefer-destructuring */

// Namespaces
import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { DataPoints, DataPointResponse, RiskMetricHasDisclaimer, PercentGrowthHasDisclaimer } from '../DataPoints';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';

// Components
import { GrowthCell, NotApplicableCell, PercentageCell, TextCell, UpDownCell } from '@/views/components/cells';

// Types
import AppRegion from '@/types/app-region';
import Dictionary from '@/types/dictionary';
import GrowthTypes from '@/types/growth-types';
import LocalizedString from '@/types/localized-string';
import RiskPeriods from '@/types/risk-periods';
import User from '@/types/user';

// Utils
import { formatMessageWithLocale } from '@/utils/i18n';
import { monthsToPeriod } from '@/utils/time';
import { PROPOSAL_DISCLAIMERS } from '../../utils/constants';
import { TIME_PERIODS } from '@/utils/constants';
import { validateUserAppRegion } from '@/utils/users';

enum CellType {
  Text,
  Percentage,
  UpDown,
}

export default class RiskMetricsDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new RiskMetricsDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    let showAdvanced: boolean = false;
    let showInformationRatio: boolean = true;
    let showCore: boolean = true;
    let showAlpha: boolean = true;
    let showRiskRating: boolean = false;
    let growthPeriods: Array<GrowthTypes> = [];
    let labelOverride: Dictionary<LocalizedString> = {};
    let showAsPercentage: Dictionary<LocalizedString> = {};

    const daoConfig: ProposalNS.RiskMetricsConfig = this.proposalElement.DAO as ProposalNS.RiskMetricsConfig;
    if (daoConfig.showAdvanced !== undefined) {
      showAdvanced = daoConfig.showAdvanced;
    }

    if (daoConfig.showInformationRatio !== undefined) {
      showInformationRatio = daoConfig.showInformationRatio;
    }

    if (daoConfig.showAlpha !== undefined) {
      showAlpha = daoConfig.showAlpha;
    }

    if (daoConfig.showCore !== undefined) {
      showCore = daoConfig.showCore;
    }

    if (daoConfig.showRiskRating !== undefined) {
      showRiskRating = daoConfig.showRiskRating;
    }

    if (daoConfig.growthPeriods !== undefined) {
      growthPeriods = daoConfig.growthPeriods;
    }

    if (daoConfig.labelOverride !== undefined) {
      labelOverride = daoConfig.labelOverride;
    }

    if (daoConfig.showAsPercentage !== undefined) {
      showAsPercentage = daoConfig.showAsPercentage;
    }

    const toolbarOption = this.getToolbarOption();

    let period: RiskPeriods = '3Y';

    if (toolbarOption !== undefined && toolbarOption.monthsRange !== undefined) {
      period = monthsToPeriod(toolbarOption.monthsRange) as RiskPeriods;
    }

    const classes = [ProposalStyleClassNames.ProposalTableContent] as string[];

    let itemDAO: ComparisonItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      if (growthPeriods.length !== 0) {
        const growthData: DataPointResponse<PercentGrowthHasDisclaimer> = DataPoints.getPercentGrowths(itemDAO.item, this.proposal.showBaseCurrencies === true);
        this.addDisclaimerKeys(growthData.disclaimerKeys, itemDAO.item);

        for (const growthPeriod of growthPeriods) {
          const fieldName = formatMessageWithLocale(`Proposal.Modules.RiskMetrics.${growthPeriod}`, this.locale);
          const value = growthData.response[growthPeriod] && growthData.response[growthPeriod].value;
          if (value != null) {
            this.addGrowthCell(itemDAO, fieldName, value, classes, growthData.response[growthPeriod].hasDisclaimer);
          } else {
            this.addNACell(itemDAO, fieldName, classes);
          }
          this.addFieldName(fieldName, this.mergeClasses(classes));
        }
      }

      const dpResponse: DataPointResponse<Dictionary<RiskMetricHasDisclaimer>> = DataPoints.getRiskMetrics(
        itemDAO.item,
        this.proposal.showBaseCurrencies === true,
      );
      const showSinceInceptionDisclaimer = period === TIME_PERIODS.INC;
      const showMaxCommonDisclaimer = period === TIME_PERIODS.CI;
      const isAppRegionUS = validateUserAppRegion(this.proposal.user as User, AppRegion.US);

      this.addDisclaimerKeys(
        [
          ...dpResponse.disclaimerKeys,
          ...(showSinceInceptionDisclaimer ? [PROPOSAL_DISCLAIMERS.SINCE_INCEPTION_DATE] : []),
          ...(showMaxCommonDisclaimer ? [PROPOSAL_DISCLAIMERS.COMMON_INCEPTION_DATE] : []),
        ],
        itemDAO.item,
      );

      const {
        beta,
        standardDeviation,
        sharpeRatio,
        maximumDrawdown,
        rSquared,
        correlationCoefficient,
        upside,
        downside,
        alpha,
        sortino,
        trackingError,
        infoRatio,
      } = dpResponse.response;

      const getLabelOverride = (key: string) => {
        const { locale } = this.proposal.options;

        return (labelOverride[key] && labelOverride[key][locale]) || this.formatMessage(key);
      };

      const getCellType = (key: string) => {
        const { locale } = this.proposal.options;

        return showAsPercentage[key] && showAsPercentage[key][locale] ? CellType.Percentage : CellType.Text;
      };

      if (!isAppRegionUS && showRiskRating) {
        const { response, disclaimerKeys } = DataPoints.getRiskRating(itemDAO.item);
        this.addDisclaimerKeys(disclaimerKeys, itemDAO.item);

        let riskRating;

        if (response) {
          // Risk Rating is currently only displayed for funds (see itemTypes), which means it can only be 100% of one rating.
          const ratingKey = Object.keys(response).find(key => response[key] === 100);
          if (ratingKey) {
            riskRating = this.formatMessage(`RiskRating.${ratingKey}`);
          }
        }

        const label = this.formatMessage('RiskMetrics.RiskRating');

        if (riskRating) {
          this.addTextCell(itemDAO, label, riskRating, classes);
        } else {
          this.addNACell(itemDAO, label, classes);
        }

        this.addFieldName(getLabelOverride('RiskMetrics.RiskRating'), this.mergeClasses(classes));
      }

      if (showCore) {
        this.addMetric(itemDAO, getLabelOverride('RiskMetrics.Beta'), beta, period, classes, getCellType('RiskMetrics.Beta'));
        this.addMetric(
          itemDAO,
          getLabelOverride('RiskMetrics.StandardDeviation'),
          standardDeviation,
          period,
          classes,
          getCellType('RiskMetrics.StandardDeviation'),
        );
        this.addMetric(itemDAO, getLabelOverride('RiskMetrics.SharpeRatio'), sharpeRatio, period, classes, getCellType('RiskMetrics.SharpeRatio'));
        this.addMetric(itemDAO, getLabelOverride('RiskMetrics.MaximumDrawdown'), maximumDrawdown, period, classes, getCellType('RiskMetrics.MaximumDrawdown'));
        this.addMetric(itemDAO, getLabelOverride('RiskMetrics.RSquared'), rSquared, period, classes, getCellType('RiskMetrics.RSquared'));
        this.addMetric(
          itemDAO,
          getLabelOverride('RiskMetrics.CorrelationCoefficient'),
          correlationCoefficient,
          period,
          classes,
          getCellType('RiskMetrics.CorrelationCoefficient'),
        );
        this.addUpDown(itemDAO, getLabelOverride('RiskMetrics.UpsideDownside'), upside, downside, period, classes);
      }

      if (showAdvanced || showAlpha) {
        this.addMetric(itemDAO, getLabelOverride('RiskMetrics.Alpha'), alpha, period, classes, getCellType('RiskMetrics.Alpha'));
      }

      if (showAdvanced) {
        this.addMetric(itemDAO, getLabelOverride('RiskMetrics.SortinoRatio'), sortino, period, classes);
        this.addMetric(itemDAO, getLabelOverride('RiskMetrics.TrackingError'), trackingError, period, classes);
        if (showInformationRatio) {
          this.addMetric(itemDAO, getLabelOverride('RiskMetrics.InformationRatio'), infoRatio, period, classes);
        }
      }
    }
  }

  // private methods

  addMetric(
    itemDAO: ComparisonItemDAO,
    label: string,
    metric: RiskMetricHasDisclaimer,
    period: RiskPeriods,
    classes: string[],
    cellType: CellType = CellType.Text,
  ) {
    if (metric != null && metric[period] != null && metric[period].value != null) {
      const hasDisclaimer: boolean | undefined = metric[period].hasDisclaimer;
      const metricPeriod = metric[period].value;
      if (cellType === CellType.Text) {
        const value: string = metricPeriod !== null ? metricPeriod.toFixed(2) : '0.00';
        this.addTextCell(itemDAO, label, value, classes, hasDisclaimer);
      } else {
        const value: number = metricPeriod !== null ? metricPeriod : 0;
        this.addPercentageCell(itemDAO, label, value, classes, hasDisclaimer);
      }
    } else {
      this.addNACell(itemDAO, label, classes);
    }
    this.addFieldName(label, this.mergeClasses(classes));
  }

  addUpDown(
    itemDAO: ComparisonItemDAO,
    label: string,
    upside: RiskMetricHasDisclaimer,
    downside: RiskMetricHasDisclaimer,
    period: RiskPeriods,
    classes: string[],
  ) {
    if (
      upside != null &&
      upside[period] != null &&
      upside[period].value != null &&
      downside != null &&
      downside[period] != null &&
      downside[period].value != null
    ) {
      const hasDisclaimer: boolean | undefined = upside[period].hasDisclaimer;
      const upsidePeriod = upside[period].value;
      const downsidePeriod = downside[period].value;

      const value: { upside: number; downside: number } = { upside: upsidePeriod || 0, downside: downsidePeriod || 0 };
      this.addUpDownCell(itemDAO, label, value, classes, hasDisclaimer);
    } else {
      this.addNACell(itemDAO, label, classes);
    }
    this.addFieldName(label, this.mergeClasses(classes));
  }

  /* eslint-disable-next-line class-methods-use-this */
  addNACell(itemDAO: ComparisonItemDAO, fieldName: string, classes: string[] = []) {
    const naField: ProposalFieldDAO = new ProposalFieldDAO(fieldName, '', this.mergeClasses(classes.concat(ProposalStyleClassNames.ProposalNAValue)));
    naField.cellType = NotApplicableCell;
    this.addFieldDAO(itemDAO, naField);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addTextCell(itemDAO: ComparisonItemDAO, fieldName: string, value: string, classes: string[] = [], hasDisclaimer?: boolean) {
    const textField: ProposalFieldDAO = new ProposalFieldDAO(fieldName, value, this.mergeClasses(classes.concat(ProposalStyleClassNames.ProposalTableContent)));
    textField.cellType = TextCell;
    textField.hasDisclaimer = hasDisclaimer;
    this.addFieldDAO(itemDAO, textField);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addPercentageCell(itemDAO: ComparisonItemDAO, fieldName: string, value: number, classes: string[] = [], hasDisclaimer?: boolean) {
    const pField: ProposalFieldDAO = new ProposalFieldDAO(fieldName, value, this.mergeClasses(classes.concat(ProposalStyleClassNames.ProposalTableContent)));
    pField.cellType = PercentageCell;
    pField.hasDisclaimer = hasDisclaimer;
    this.addFieldDAO(itemDAO, pField);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addGrowthCell(itemDAO: ComparisonItemDAO, fieldName: string, value: number, classes: string[] = [], hasDisclaimer?: boolean) {
    const pField: ProposalFieldDAO = new ProposalFieldDAO(fieldName, value, this.mergeClasses(classes.concat(ProposalStyleClassNames.ProposalTableContent)));
    pField.cellType = GrowthCell;
    pField.hasDisclaimer = hasDisclaimer;
    this.addFieldDAO(itemDAO, pField);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addUpDownCell(itemDAO: ComparisonItemDAO, fieldName: string, value: { upside: number; downside: number }, classes: string[] = [], hasDisclaimer?: boolean) {
    const udField: ProposalFieldDAO = new ProposalFieldDAO(fieldName, value, this.mergeClasses(classes.concat(ProposalStyleClassNames.ProposalTableContent)));
    udField.cellType = UpDownCell;
    udField.hasDisclaimer = hasDisclaimer;
    this.addFieldDAO(itemDAO, udField);
  }

  mergeClasses = (classes: string[]) => classes.join(' ');
}
